import type { CartResponse, CartUpdatePayload, VoucherOptions } from '../interfaces/Cart.model';

export type ApiError = {
    message: string;
    code?: string;
};

type AdministrativeArea = {
    code: string;
    name: string;
};

const CART_FRAGMENT = `
    fragment CartFields on Cart {
        id
        items {
            id
            sku
            price
            quantity
            subtotal
            discount
            total
            product {
                ... on CartEventProduct {
                    id
                    title
                    type
                    thumbnail
                    availableCapacity
                    startDate
                    endDate
                    eventType {
                        id
                        title
                        aantalPersonenRegel
                    }
                }
                ... on CartVoucherProduct {
                    id
                    title
                    type
                    thumbnail
                    options {
                        from
                        to
                        amount
                    }
                }
                ... on CartRegularProduct {
                    id
                    title
                    type
                    thumbnail
                }
            }
        }
        totals {
            itemSubtotal
            totalDiscount
            itemTotal
            total
            totalTax
            totalTaxIncluded
            outstandingBalance
        }
        discounts {
            type
            code
            included
            discount
            description
        }
    }
`;

const CART_QUERY = `
    query GetCart {
        cart {
            ...CartFields
        }
    }
    ${CART_FRAGMENT}
`;

const CART_MUTATIONS = {
    UPDATE_CART_ITEM: `
        mutation UpdateCartItem($input: UpdateCartItemInput!) {
            updateCartItem(input: $input) {
                ...CartFields
            }
        }
        ${CART_FRAGMENT}
    `,
    UPDATE_CART_CUSTOMER: `
        mutation UpdateCartCustomer($input: UpdateCartCustomerInput!) {
            updateCartCustomer(input: $input) {
                ...CartFields
            }
        }
        ${CART_FRAGMENT}
    `,
    ADD_VOUCHER_CODE: `
        mutation AddVoucherCode($code: String!) {
            addVoucherCode(code: $code) {
                ...CartFields
            }
        }
        ${CART_FRAGMENT}
    `,
    REMOVE_VOUCHER_CODE: `
        mutation RemoveVoucherCode($code: String!, $type: String!) {
            removeVoucherCode(code: $code, type: $type) {
                ...CartFields
            }
        }
        ${CART_FRAGMENT}
    `,
};

let cachedCsrfToken: string | null = null;
export const getCsrfToken = (): string => {
    if (cachedCsrfToken !== null) return cachedCsrfToken;
    cachedCsrfToken =
        document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';
    return cachedCsrfToken;
};

export const fetchWithCsrf = async <T>(url: string, options: RequestInit = {}): Promise<T> => {
    const defaultHeaders = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': getCsrfToken(),
        'X-Requested-With': 'XMLHttpRequest',
    };

    try {
        const response = await fetch(url, {
            ...options,
            credentials: 'same-origin',
            headers: {
                ...defaultHeaders,
                ...options.headers,
            },
        });

        const data = await response.json();

        if (!response.ok) {
            console.error('API Error:', {
                status: response.status,
                data: data,
            });
            if (data.errors) {
                const errorEntries = Object.entries(data.errors);
                if (errorEntries.length > 0) {
                    const [field, messages] = errorEntries[0];
                    throw new Error(Array.isArray(messages) ? messages[0] : messages, {
                        cause: field,
                    });
                }
            }
            throw new Error(data.message || 'Request failed');
        }

        return data as T;
    } catch (error) {
        if (error instanceof Error) {
            throw error;
        }
        throw new Error('An unknown error occurred');
    }
};

const executeGraphQL = async <T>(
    query: string,
    variables?: Record<string, unknown>
): Promise<T> => {
    return fetchWithCsrf<T>('/api/graphql', {
        method: 'POST',
        body: JSON.stringify({
            query,
            variables,
        }),
    });
};

export const api = {
    cart: {
        get: async () => {
            const response = await executeGraphQL<CartResponse>(CART_QUERY);
            return response.data;
        },
        update: async (payload: CartUpdatePayload) => {
            const response = await executeGraphQL<CartResponse>(
                CART_MUTATIONS.UPDATE_CART_CUSTOMER,
                {
                    input: payload,
                }
            );
            return response.data;
        },
        updateQuantity: async (lineItemId: number, quantity: number) => {
            const response = await executeGraphQL<CartResponse>(CART_MUTATIONS.UPDATE_CART_ITEM, {
                input: {
                    lineItemId: parseInt(String(lineItemId), 10),
                    qty: quantity,
                },
            });
            return response.data;
        },
        addVoucher: async (voucherData: VoucherOptions) => {
            const response = await fetchWithCsrf<CartResponse>(
                '/actions/commerce/cart/update-cart',
                {
                    method: 'POST',
                    body: JSON.stringify(voucherData),
                }
            );
            return response.data;
        },
        getPaymentHashes: async () => {
            const response = await fetch('/api/payment-hashes', {
                headers: {
                    Accept: 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch payment hashes');
            }

            const data = await response.json();
            if (!data.success) {
                throw new Error(data.error || 'Failed to fetch payment hashes');
            }

            return data.hashes;
        },
        getAdministrativeAreas: async (countryCode: string): Promise<AdministrativeArea[]> => {
            const response = await fetch(
                `/api/administrative-areas?countryCode=${encodeURIComponent(countryCode)}`,
                {
                    headers: {
                        Accept: 'application/json',
                    },
                }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch administrative areas');
            }

            const data = await response.json();
            if (!data.success) {
                throw new Error(data.error || 'Failed to fetch administrative areas');
            }

            return data.areas as AdministrativeArea[];
        },
        addVoucherCode: async (code: string) => {
            const response = await executeGraphQL<CartResponse>(CART_MUTATIONS.ADD_VOUCHER_CODE, {
                code,
            });

            if (response.errors) {
                throw new Error('Failed to add voucher code');
            }

            return response.data;
        },
        removeVoucherCode: async (code: string, type: string) => {
            const response = await executeGraphQL<CartResponse>(
                CART_MUTATIONS.REMOVE_VOUCHER_CODE,
                {
                    type,
                    code,
                }
            );
            return response.data;
        },
    },
};
